import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toAdminObject = (dbAdmin) => ({
  id: dbAdmin?.id,
  email: dbAdmin?.email,
  fullName: dbAdmin?.full_name,
  role: dbAdmin?.role,
  practitionerId: dbAdmin?.practitioner_id,
  createdAt: dbAdmin?.created_at,
  updatedAt: dbAdmin?.updated_at,
  lastSignInAt: dbAdmin?.last_sign_in_at
});

export const adminService = {
  /**
   * Get all super admins
   */
  async getAllSuperAdmins() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('role', 'super_admin')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data?.map(toAdminObject) || [];
    } catch (error) {
      console.error('Error fetching super admins:', error);
      throw error;
    }
  },

  /**
   * Create new super admin
   */
  async createSuperAdmin(email, password, fullName) {
    try {
      // Get current session
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      // Call Edge Function to create admin user
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/create-super-admin`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            full_name: fullName
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de la création de l\'admin');
      }

      return result;
    } catch (error) {
      console.error('Error creating super admin:', error);
      throw error;
    }
  },

  /**
   * Update super admin
   */
  async updateSuperAdmin(adminId, updates) {
    try {
      const dbUpdates = {
        full_name: updates?.fullName,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', adminId)?.eq('role', 'super_admin')?.select()?.single();

      if (error) throw error;
      return toAdminObject(data);
    } catch (error) {
      console.error('Error updating super admin:', error);
      throw error;
    }
  },

  /**
   * Delete super admin
   */
  async deleteSuperAdmin(adminId) {
    try {
      // Get current session
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      // Call Edge Function to delete admin
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/delete-super-admin`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            admin_id: adminId
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de la suppression de l\'admin');
      }

      return result;
    } catch (error) {
      console.error('Error deleting super admin:', error);
      throw error;
    }
  },

  /**
   * Reset admin password
   */
  async resetAdminPassword(adminId, newPassword) {
    try {
      // Get current session
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      // Call Edge Function to reset password
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/reset-admin-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            admin_id: adminId,
            new_password: newPassword
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de la réinitialisation du mot de passe');
      }

      return result;
    } catch (error) {
      console.error('Error resetting admin password:', error);
      throw error;
    }
  },

  /**
   * Check if email is available
   */
  async checkEmailAvailability(email) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id')?.eq('email', email)?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      return !data; // true if available, false if taken
    } catch (error) {
      console.error('Error checking email availability:', error);
      throw error;
    }
  }
};